// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoLine from "@store/Library/Components/Toolkit/Icons/Info/InfoLine";
import { ZeroPriceDisplayDecision } from "@store/Framework/Common/Utilities/resolveZeroPriceDisplay";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, ReactNode } from "react";
import { css } from "styled-components";

export type ZeroPriceContactUsLayout = "inline" | "block";

export interface ZeroPriceContactUsActionStyles {
    /** Outer flex wrapper for consumers who wrap the masked price text and this action together. */
    outerWrapper?: InjectableCss;
    /** Wrapper when layout is "inline" (default). */
    actionWrapper?: InjectableCss;
    /** Wrapper when layout is "block". */
    blockActionWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const zeroPriceContactUsActionStyles: ZeroPriceContactUsActionStyles = {
    outerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0 ${pxToRem(8)};
        `,
    },
    actionWrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
            gap: ${pxToRem(4)};
        `,
    },
    blockActionWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            flex-basis: 100%;
        `,
    },
    link: {
        mergeCss: true,
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    tooltip: {
        iconProps: {
            size: 16,
            src: InfoLine,
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
                line-height: 0;
            `,
        },
    },
};

const styles = zeroPriceContactUsActionStyles;

interface ZeroPriceContactUsActionProps {
    decision?: ZeroPriceDisplayDecision;
    /** Controls whether the action renders inline or on a new line below the price. */
    layout?: ZeroPriceContactUsLayout;
    actionWrapper?: InjectableCss;
    linkProps?: LinkPresentationProps;
    linkDataTestSelector?: string;
    tooltipProps?: TooltipPresentationProps;
    renderTooltip?: (tooltipText: string) => ReactNode;
}

const ZeroPriceContactUsAction: FC<ZeroPriceContactUsActionProps> = ({
    decision,
    layout = "inline",
    actionWrapper,
    linkProps,
    linkDataTestSelector,
    tooltipProps,
    renderTooltip,
}) => {
    const supportUrl = decision?.actionState === "link" ? decision.supportUrl : undefined;
    const tooltipText = decision?.tooltipMessageKey ? siteMessage(decision.tooltipMessageKey).toString() : undefined;

    const resolvedTooltipRender =
        renderTooltip ?? ((text: string) => <Tooltip {...styles.tooltip} {...tooltipProps} text={text} />);

    if (!supportUrl && !tooltipText) {
        return null;
    }

    const mergedLinkProps = { ...styles.link, ...linkProps };
    const baseWrapper = layout === "block" ? styles.blockActionWrapper : styles.actionWrapper;
    const mergedWrapper = actionWrapper ? { ...baseWrapper, ...actionWrapper } : baseWrapper;

    return (
        <StyledWrapper {...mergedWrapper}>
            {supportUrl ? (
                <Link {...mergedLinkProps} href={supportUrl} data-test-selector={linkDataTestSelector}>
                    {translate("Contact Us")}
                </Link>
            ) : null}
            {tooltipText ? resolvedTooltipRender(tooltipText) : null}
        </StyledWrapper>
    );
};

export default ZeroPriceContactUsAction;
